import qs from "qs";
import { useLocation } from "react-router-dom";

/**
 * Kiosk / fullscreen presentation mode.
 *
 * Kiosk mode hides the Console chrome (top bar + quick-access sidebar) so the
 * routed screen — primarily a `dashboard` / `view` — fills the viewport, for
 * wall-boards, always-on displays and focused viewing. It is expressed purely as
 * a URL query flag (`?kiosk=1`), so a kiosk URL is bookmarkable and reloadable
 * and dashboard auto-refresh keeps working. Entering optionally drives the
 * browser Fullscreen API (possible only from a user gesture); a directly-loaded
 * kiosk URL still hides the chrome even when fullscreen cannot be requested.
 */
export const KIOSK_QUERY_FLAG = "kiosk";

interface MinimalLocation {
    pathname: string;
    search: string;
    hash: string;
}

interface MinimalHistory {
    push: (location: MinimalLocation) => void;
}

const parse = (search: string): Record<string, unknown> =>
    qs.parse(search.startsWith("?") ? search.substring(1) : search) as Record<string, unknown>;

/** Whether a raw `location.search` string carries the kiosk flag. */
export const isKioskSearch = (search: string): boolean => KIOSK_QUERY_FLAG in parse(search);

/** React hook: is the current route in kiosk mode? */
export const useIsKiosk = (): boolean => isKioskSearch(useLocation().search);

const requestFullscreenSafe = (): void => {
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) {
        // May reject when not triggered by a user gesture or when disabled by the
        // browser; kiosk chrome-hiding still applies regardless.
        void el.requestFullscreen().catch(() => undefined);
    }
};

const exitFullscreenSafe = (): void => {
    if (document.fullscreenElement && document.exitFullscreen) {
        void document.exitFullscreen().catch(() => undefined);
    }
};

/** Enter kiosk mode: add `?kiosk=1` and (optionally) request browser fullscreen. */
export const enterKiosk = (
    history: MinimalHistory,
    location: MinimalLocation,
    options: { fullscreen?: boolean } = {}
): void => {
    const { fullscreen = true } = options;
    const params = parse(location.search);
    if (!(KIOSK_QUERY_FLAG in params)) {
        params[KIOSK_QUERY_FLAG] = "1";
        history.push({
            pathname: location.pathname,
            search: qs.stringify(params),
            hash: location.hash,
        });
    }
    if (fullscreen) requestFullscreenSafe();
};

/** Leave kiosk mode: drop the kiosk flag and exit browser fullscreen if active. */
export const exitKiosk = (history: MinimalHistory, location: MinimalLocation): void => {
    const params = parse(location.search);
    if (KIOSK_QUERY_FLAG in params) {
        delete params[KIOSK_QUERY_FLAG];
        history.push({
            pathname: location.pathname,
            search: qs.stringify(params),
            hash: location.hash,
        });
    }
    exitFullscreenSafe();
};
